import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, SlidersHorizontal, MoreVertical,
  Package, Star, Edit3, Trash2, ChevronLeft, LayoutGrid,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToHostProducts,
  deleteProduct,
  Product,
} from "../../redux/productSlice";
import { fetchAllProductSummaries } from "../../redux/productRatingSlice";
import { electronicsCategories } from "../../UNIVERSAL/CATEGORIES/ElectronicsCategories";

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════ */
const T = {
  pageBg:   "#08060F",
  cardBg:   "#100D1C",
  surface:  "#1A1530",
  grad:     "linear-gradient(90deg, #007BFF 0%, #6A00FF 50%, #FF00D4 100%)",
  blue:     "#007BFF",
  purple:   "#6A00FF",
  pink:     "#FF00D4",
  white:    "#FFFFFF",
  offwhite: "#E8E4FF",
  muted:    "#8B82A8",
  mutedDim: "#5A5278",
  border:   "rgba(106,0,255,0.22)",
  gold:     "#FFD60A",
  red:      "#FF3B5C",
  green:    "#00E676",
};

const GRAD = T.grad;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{display:none;}
  input,button{font-family:inherit;}
  body{background:#08060F;}

  @keyframes gradSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes pulseDot{0%,100%{opacity:1;transform:scale(1);}50%{opacity:.5;transform:scale(1.3);}}

  input::placeholder{color:rgba(255,255,255,.28);}

  .p-row{
    transition:background .2s,box-shadow .2s;
    cursor:pointer;
  }
  .p-row:hover{
    background:rgba(106,0,255,.12)!important;
    box-shadow:0 0 0 1px rgba(106,0,255,.4);
  }

  .cat-chip{
    display:flex;flex-direction:column;align-items:flex-start;
    padding:12px 14px;border-radius:16px;
    border:1.5px solid rgba(106,0,255,.22);
    background:#100D1C;cursor:pointer;flex-shrink:0;min-width:90px;
    transition:border-color .2s,background .2s,box-shadow .2s;
  }
  .cat-chip:hover{border-color:rgba(106,0,255,.55);background:rgba(106,0,255,.12);}
  .cat-chip.active{
    border-color:transparent;
    background:linear-gradient(#100D1C,#100D1C) padding-box,
               linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4) border-box;
    border:1.5px solid transparent;
    box-shadow:0 0 16px rgba(106,0,255,.35);
  }

  .price-pill{
    display:inline-flex;align-items:center;
    background:linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4);
    background-size:200% 100%;animation:gradSpin 3s ease infinite;
    border-radius:8px;padding:5px 13px;
    font-weight:900;color:#fff;font-size:15px;letter-spacing:-0.4px;
    box-shadow:0 2px 12px rgba(106,0,255,.4);
  }

  .dot-menu{
    position:absolute;right:12px;top:52px;
    background:#1A1530;border:1px solid rgba(106,0,255,.3);
    border-radius:12px;overflow:hidden;z-index:50;
    box-shadow:0 12px 40px rgba(0,0,0,.5);min-width:160px;
  }
  .dot-menu-item{
    display:flex;align-items:center;gap:10px;padding:11px 16px;
    font-size:13px;font-weight:600;color:#E8E4FF;cursor:pointer;
    border:none;background:none;width:100%;text-align:left;
    transition:background .15s;
  }
  .dot-menu-item:hover{background:rgba(106,0,255,.2);}
  .dot-menu-item.danger{color:#FF3B5C;}
  .dot-menu-item.danger:hover{background:rgba(255,59,92,.15);}
`;

/* ─── helpers ─── */
const fmtPrice = (n: number) =>
  `€${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ─── types ─── */
interface CatChip {
  id:    number | "all";
  name:  string;
  icon:  any;
  color: string;
  count: number;
}

interface RowCardProps {
  product:  Product;
  rating?:  { avg: number; count: number } | null;
  onEdit:   () => void;
  onDelete: () => void;
}

/* ═══════════════════════════════════════════════════════════
   PRODUCT ROW CARD
═══════════════════════════════════════════════════════════ */
const ProductRow: React.FC<RowCardProps> = ({ product, rating, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const thumb = product.photos?.[0] ?? null;

  return (
    <motion.div
      className="p-row"
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      style={{
        position: "relative",
        background: T.cardBg,
        border: `1.5px solid ${T.border}`,
        borderRadius: 18,
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: 12,
        overflow: "visible",
      }}
      onClick={onEdit}
    >
      {/* thumbnail */}
      <div style={{
        width: 90, height: 90, borderRadius: 12, overflow: "hidden",
        background: "linear-gradient(135deg, rgba(0,123,255,.12), rgba(106,0,255,.08))",
        flexShrink: 0, position: "relative",
      }}>
        {thumb ? (
          <img src={thumb} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Package size={28} color={T.purple} style={{ opacity: 0.5 }} />
          </div>
        )}

        {/* sold badge */}
        {(product as any).sold > 0 && (
          <div style={{
            position: "absolute", bottom: 6, left: 6,
            background: "rgba(8,6,15,.85)", backdropFilter: "blur(6px)",
            border: "1px solid rgba(0,123,255,.45)",
            borderRadius: 999, padding: "2px 7px",
            display: "flex", alignItems: "center", gap: 4,
          }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: T.blue, animation: "pulseDot 2s infinite" }} />
            <span style={{ fontSize: 9, fontWeight: 700, color: T.offwhite }}>
              {(product as any).sold >= 1000
                ? `${((product as any).sold / 1000).toFixed(0)}K`
                : (product as any).sold} sold
            </span>
          </div>
        )}
      </div>

      {/* info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 15, color: T.white, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: -0.2 }}>
          {product.name || "Untitled Product"}
        </div>

        {(product as any).description && (
          <div style={{ fontSize: 12, color: T.muted, marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {(product as any).description}
          </div>
        )}

        <span className="price-pill" style={{ fontSize: 14 }}>
          {fmtPrice(product.rate ?? 0)}
        </span>

        {rating && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
            <Star size={11} fill={T.gold} color={T.gold} />
            <span style={{ color: T.gold, fontWeight: 800, fontSize: 12 }}>{rating.avg.toFixed(1)}</span>
            <span style={{ color: T.mutedDim, fontSize: 11 }}>| {rating.count.toLocaleString()} reviews</span>
          </div>
        )}

        <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          {product.isPublished ? (
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", background: "rgba(0,230,118,.15)", color: T.green, padding: "2px 8px", borderRadius: 999, border: "1px solid rgba(0,230,118,.3)" }}>LIVE</span>
          ) : (
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", background: "rgba(255,255,255,.06)", color: T.mutedDim, padding: "2px 8px", borderRadius: 999, border: "1px solid rgba(255,255,255,.1)" }}>DRAFT</span>
          )}
          {product.stock !== undefined && (
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", background: "rgba(106,0,255,.12)", color: T.purple, padding: "2px 8px", borderRadius: 999, border: "1px solid rgba(106,0,255,.25)" }}>
              {product.stock} in stock
            </span>
          )}
        </div>
      </div>

      {/* 3-dot button */}
      <button
        onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); }}
        style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: T.mutedDim, flexShrink: 0 }}
      >
        <MoreVertical size={18} />
      </button>

      {/* 3-dot dropdown */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <div
              style={{ position: "fixed", inset: 0, zIndex: 49 }}
              onClick={e => { e.stopPropagation(); setMenuOpen(false); }}
            />
            <motion.div
              className="dot-menu"
              initial={{ opacity: 0, scale: 0.92, y: -6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: -6 }}
              transition={{ duration: 0.15 }}
              onClick={e => e.stopPropagation()}
            >
              <button className="dot-menu-item" onClick={() => { setMenuOpen(false); onEdit(); }}>
                <Edit3 size={14} color={T.purple} /> Edit Product
              </button>
              <button className="dot-menu-item danger" onClick={() => { setMenuOpen(false); onDelete(); }}>
                <Trash2 size={14} /> Delete
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN — Products
═══════════════════════════════════════════════════════════ */
const Products: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const authUserId = useSelector((s: RootState) => s.auth.user?.userId);
  const hostId     = userId ?? authUserId ?? "";

  const products        = useSelector((s: RootState) => s.products.products);
  const ratingSummaries = useSelector((s: RootState) => s.productRatings.summaries);

  const [search,    setSearch]    = useState("");
  const [activeCat, setActiveCat] = useState<number | "all">("all");

  /* ── listeners ── */
  useEffect(() => {
    if (!hostId) return;
    dispatch(listenToHostProducts({ hostId }));
    dispatch(fetchAllProductSummaries());
  }, [dispatch, hostId]);

  /* ── category chips built from live product data ── */
  const catChips: CatChip[] = useMemo(() => {
    const all: CatChip = {
      id: "all", name: "All Products",
      icon: LayoutGrid, color: T.blue,
      count: products.length,
    };
    const usedIds = new Set(products.map((p: any) => p.categoryId).filter(Boolean));
    const cats = (electronicsCategories as any[])
      .filter(c => usedIds.has(c.id))
      .map(c => ({
        id:    c.id   as number,
        name:  c.name as string,
        icon:  c.icon,
        color: T.purple,
        count: products.filter((p: any) => p.categoryId === c.id).length,
      }));
    return [all, ...cats];
  }, [products]);

  /* ── filtered list ── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p: any) => {
      if (activeCat !== "all" && p.categoryId !== activeCat) return false;
      if (q && !`${p.name ?? ""} ${p.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [products, activeCat, search]);

  const handleDelete = (listingId: string) => {
    if (!window.confirm("Delete this product permanently?")) return;
    dispatch(deleteProduct(listingId));
  };

  const goToAdd = (listingId?: string) =>
    navigate(listingId ? `/AddProducts/${listingId}` : "/AddProducts");

  const publishedCount = products.filter((p: any) => p.isPublished).length;
  const draftCount     = products.filter((p: any) => !p.isPublished).length;

  /* ─── RENDER ─── */
  return (
    <div style={{ minHeight: "100vh", background: T.pageBg, fontFamily: "'Plus Jakarta Sans',sans-serif", paddingBottom: 40 }}>
      <style>{CSS}</style>

      {/* ── PAGE HEADER ── */}
      <div style={{ background: T.cardBg, borderBottom: `1px solid ${T.border}`, padding: "20px 20px 0" }}>

        {/* gradient accent top */}
        <div style={{ height: 3, background: GRAD, backgroundSize: "200% 100%", animation: "gradSpin 4s linear infinite", marginBottom: 16, borderRadius: 999, marginTop: -20, marginLeft: -20, marginRight: -20 }} />

        {/* title row */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 4 }}>
          <div>
            <h1 style={{ fontWeight: 900, fontSize: 26, color: T.white, letterSpacing: -0.5, lineHeight: 1.1 }}>
              My Electronics
            </h1>
            <p style={{ fontSize: 13, color: T.muted, marginTop: 3 }}>
              Manage and monitor your electronics
            </p>
          </div>

          {/* ── Add Product button ── */}
          <motion.button
            whileTap={{ scale: 0.94 }}
            onClick={() => goToAdd()}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              background: GRAD, backgroundSize: "200% 100%",
              animation: "gradSpin 3s ease infinite",
              border: "none", borderRadius: 999,
              padding: "11px 20px",
              color: "#fff", fontWeight: 800, fontSize: 14,
              cursor: "pointer",
              boxShadow: "0 4px 20px rgba(106,0,255,.45)",
              flexShrink: 0,
              fontFamily: "'Plus Jakarta Sans',sans-serif",
            }}
          >
            <Plus size={16} /> Add Product
          </motion.button>
        </div>

        {/* stat pills */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, marginTop: 12, flexWrap: "wrap" }}>
          {[
            { label: "Live",   val: publishedCount,   col: T.green   },
            { label: "Drafts", val: draftCount,       col: T.mutedDim },
            { label: "Total",  val: products.length,  col: T.purple  },
          ].map(s => (
            <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 999, padding: "5px 13px" }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: s.col }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: T.offwhite }}>{s.val}</span>
              <span style={{ fontSize: 11, color: T.mutedDim }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* search + filter */}
        <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
          <div style={{
            flex: 1, display: "flex", alignItems: "center", gap: 8,
            background: "rgba(255,255,255,.06)", borderRadius: 999,
            border: `1.5px solid ${T.border}`,
            padding: "10px 16px",          /* ← fixed: was broken backtick `"10px 16px`" */
          }}>
            <Search size={15} color={T.mutedDim} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search electronics…"
              style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 14, color: T.white, fontFamily: "inherit" }}
            />
          </div>
          <button style={{
            display: "flex", alignItems: "center", gap: 7,
            background: "rgba(106,0,255,.15)", border: "1.5px solid rgba(106,0,255,.4)",
            borderRadius: 999, padding: "10px 18px",
            color: T.offwhite, fontWeight: 700, fontSize: 13, cursor: "pointer", flexShrink: 0,
          }}>
            <SlidersHorizontal size={14} color={T.purple} /> Filter
          </button>
        </div>

        {/* category chips — horizontal scroll */}
        <div style={{ display: "flex", gap: 10, overflowX: "auto", scrollbarWidth: "none" as const, paddingBottom: 16 }}>
          {catChips.map(chip => {
            const Icon = chip.icon;
            return (
              <button
                key={chip.id}
                className={`cat-chip${activeCat === chip.id ? " active" : ""}`}
                onClick={() => setActiveCat(chip.id)}
              >
                <div style={{ marginBottom: 6 }}>
                  {Icon && <Icon size={20} color={activeCat === chip.id ? T.blue : chip.color} strokeWidth={1.5} />}
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: activeCat === chip.id ? T.offwhite : T.muted, whiteSpace: "nowrap" }}>
                  {chip.name}
                </span>
                <span style={{ fontSize: 18, fontWeight: 900, color: activeCat === chip.id ? T.white : T.offwhite, lineHeight: 1.1, marginTop: 2 }}>
                  {chip.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── PRODUCT LIST ── */}
      <div style={{ padding: "16px 16px 0" }}>
        {filtered.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            style={{ textAlign: "center", paddingTop: 80 }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>⚡</div>
            <h2 style={{ fontWeight: 900, fontSize: 20, color: T.white, marginBottom: 8 }}>
              {products.length === 0 ? "No products yet" : "No results found"}
            </h2>
            <p style={{ color: T.muted, marginBottom: 28, fontSize: 14, lineHeight: 1.7 }}>
              {products.length === 0
                ? "Add your first product to start selling."
                : "Try adjusting your search or filter."}
            </p>
            {products.length === 0 && (
              <button
                onClick={() => goToAdd()}
                style={{ padding: "13px 32px", borderRadius: 999, border: "none", background: GRAD, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                + Add First Product
              </button>
            )}
          </motion.div>
        ) : (
          <AnimatePresence>
            {filtered.map((product: Product) => {
              const sum    = ratingSummaries[product.listingId];
              const rating = sum && sum.totalReviews > 0
                ? { avg: sum.avgRating, count: sum.totalReviews }
                : null;
              return (
                <ProductRow
                  key={product.listingId}
                  product={product}
                  rating={rating}
                  onEdit={() => goToAdd(product.listingId)}
                  onDelete={() => handleDelete(product.listingId)}
                />
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default Products;